import { inject, Injectable, PLATFORM_ID } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'
import { TranslatedRouter } from '@endeavour/ngx-translate-router'
import { firstValueFrom, isObservable } from 'rxjs'

/**
 * Marker on the RouterConfigLoader (or its prototype) so we do not stack
 * Promise wrappers when Angular22TranslatedRouter is constructed more than once
 * (tests, HMR, SSR warm starts).
 */
const LOAD_CHILDREN_PROMISE_PATCH = Symbol.for('eekhoorn.angular22TranslatedRouter.loadChildrenPatch')

/**
 * Angular 22's RouterConfigLoader.loadChildren returns a Promise.
 * @endeavour/ngx-translate-router@16.2.2 still patches it to return an Observable,
 * so `await loadChildren(...)` resolves to the Observable itself and `cfg.routes`
 * is undefined — which crashes in containsEmptyPathMatches / split.
 *
 * Remove this shim once ngx-translate-router returns Promises for Angular 22+.
 * Track upstream: endeavour/ngx-translate-router Angular 22 loadChildren Promise support.
 */
@Injectable()
export class Angular22TranslatedRouter extends TranslatedRouter {
    constructor() {
        super()

        const platformId = inject(PLATFORM_ID)
        const isBrowser = isPlatformBrowser(platformId)
        // Same resolution as TranslatedRouter (prototype in browser for preloading; instance for SSR).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const navigationTransitions = (this as any).navigationTransitions

        if (! navigationTransitions?.configLoader) {
            throw new Error(
                'Angular22TranslatedRouter: navigationTransitions.configLoader is missing. '
                + 'Angular Router internals may have changed; update or remove this shim.',
            )
        }

        const configLoaderInstance = navigationTransitions.configLoader
        const configLoader = isBrowser
            ? Object.getPrototypeOf(configLoaderInstance)
            : configLoaderInstance

        if (! configLoader || typeof configLoader.loadChildren !== 'function') {
            throw new Error(
                'Angular22TranslatedRouter: could not resolve a loadChildren target. '
                + 'Angular Router internals may have changed; update or remove this shim.',
            )
        }

        if (configLoader[LOAD_CHILDREN_PROMISE_PATCH]) {
            return
        }

        const observableLoadChildren = configLoader.loadChildren
        configLoader.loadChildren = function (
            this: unknown,
            parentInjector: unknown,
            route: unknown,
        ): Promise<unknown> {
            const result = observableLoadChildren.call(this, parentInjector, route)
            if (isObservable(result)) {
                return firstValueFrom(result)
            }
            return Promise.resolve(result)
        }
        configLoader[LOAD_CHILDREN_PROMISE_PATCH] = true
    }
}
